import {
  Box,
  Button,
  FormHelperText,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  dangerZoneConfirmFieldWidthPx,
  settingsDangerZoneConfirmButtonSx,
  settingsDangerZoneConfirmFieldSx,
  settingsDangerZonePanelSx
} from "~/features/settings/settings.styles";

import { SettingsSectionWrapper } from "./SettingsSection";

type DangerZoneWrapperProps = {
  title: string;
  children: ReactNode;
};

export function DangerZoneWrapper(props: DangerZoneWrapperProps) {
  const { title, children } = props;
  return (
    <SettingsSectionWrapper title={title} titleSx={{ fontSize: "1.5rem" }}>
      <Stack spacing={2} sx={{ width: "100%" }}>
        {children}
      </Stack>
    </SettingsSectionWrapper>
  );
}

export type SettingsDangerZoneRowProps = {
  title: string;
  hint: string;
  confirmationPhrase: string;
  confirmationValue: string;
  onConfirmationChange: (value: string) => void;
  onAction: () => void;
  fieldDisabled?: boolean;
  actionDisabled?: boolean;
};

export function SettingsDangerZoneRow(props: SettingsDangerZoneRowProps) {
  const { t } = useTranslation();
  const {
    title,
    hint,
    confirmationPhrase,
    confirmationValue,
    onConfirmationChange,
    onAction,
    fieldDisabled,
    actionDisabled
  } = props;

  const confirmationHelperText = t("type confirmation phrase to continue", {
    phrase: confirmationPhrase.toUpperCase()
  });
  const cautionHint = `${t("caution").toUpperCase()}: ${hint}`;

  return (
    <Box sx={(theme) => settingsDangerZonePanelSx(theme)}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <Stack spacing={0.5} flex={1}>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: "normal" }}>
            {title}
          </Typography>
          <Typography variant="body2">{cautionHint}</Typography>
        </Stack>
        <Box
          sx={{
            display: "grid",
            flexShrink: 0,
            gridTemplateColumns: `${dangerZoneConfirmFieldWidthPx}px auto`,
            gridTemplateRows: "auto auto",
            columnGap: 1.5,
            rowGap: 0.5,
            alignItems: "center"
          }}
        >
          <TextField
            size="small"
            placeholder={confirmationPhrase}
            value={confirmationValue}
            onChange={(e) => onConfirmationChange(e.target.value)}
            disabled={fieldDisabled}
            sx={{
              ...settingsDangerZoneConfirmFieldSx,
              gridColumn: 1,
              gridRow: 1
            }}
            slotProps={{
              htmlInput: {
                "aria-label": confirmationPhrase
              }
            }}
          />
          <Button
            color="error"
            variant="contained"
            size="small"
            disabled={actionDisabled}
            onClick={onAction}
            sx={(theme) => ({
              gridColumn: 2,
              gridRow: 1,
              ...settingsDangerZoneConfirmButtonSx(
                theme,
                Boolean(actionDisabled)
              )
            })}
          >
            {t("confirm")}
          </Button>
          <FormHelperText
            sx={{
              m: 0,
              lineHeight: 1.3,
              gridColumn: 1,
              gridRow: 2,
              alignSelf: "start",
              color: "text.secondary"
            }}
          >
            {confirmationHelperText}
          </FormHelperText>
        </Box>
      </Stack>
    </Box>
  );
}
